/**
 * Control tower — the banner, the six-tile KPI strip, plan vs actual, and the
 * three actions, in the published page's order.
 *
 * TWO RULES DECIDE EVERY FIGURE HERE.
 *
 * The basis ladder: portal actual > crawl estimate > crawl actual. The headline
 * GMV and units are the brand's own portal download, which is what the client
 * can check against their own screen; the crawl is gated to the city-days it
 * observed and undercounts.
 *
 * A percentage is never averaged. OSA and fill rate are pooled by the database
 * — SUM(numerator) / SUM(denominator) over the window — so a city with four
 * hundred stores outweighs one with four, which averaging the percentages would
 * not.
 */
import { Delta, Kpi, Kpis, fmt } from "../ds";
import { ErrorBox, State } from "../Panels";
import { useReport } from "../useReport";
import { ActionsCard } from "./Actions";
import { PlanStrip } from "./PlanStrip";
import {
  type Basis,
  type OpsWindow,
  bandColor,
  canCompare,
  comparisonLabel,
  daysBetween,
  num,
  str,
} from "./opsLib";
import type { DateRange } from "@/lib/dateRange";

export function ControlTower({
  customer,
  range,
  basis,
}: {
  customer: string;
  range: DateRange;
  basis: Basis;
}) {
  const q = useReport("/v1/reports/ops/control-tower", customer, range);

  if (q.isLoading) return <State>Loading the control tower…</State>;
  if (q.isError) return <ErrorBox>{q.error.message}</ErrorBox>;

  const periods = q.data?.periods ?? [];
  const cur = periods.find((p) => p["period"] === "current");
  const prv = periods.find((p) => p["period"] === "previous");
  if (!cur) return <State>No data for this brand in this window.</State>;

  const w: OpsWindow | undefined = q.data?.window;
  const days = w ? daysBetween(w.start, w.end) : null;
  const cmpLabel = comparisonLabel(w);
  const compare = canCompare(w) && prv !== undefined;

  /*
   * WHICH MEASUREMENT EACH TILE IS ON, taken from the server rather than
   * asserted here.
   *
   * `basis: 'portal_actual'` means the route overwrote gmv/units/drr with the
   * partner's portal download, which exists at MRP only. So:
   *
   *   MRP  -> the portal actual, and there is NO previous-period portal figure
   *           to compare it with. The tile says "no comparison" rather than
   *           quietly dividing a portal number by a crawl one, which would
   *           report the 15% gap between the two measurements as growth.
   *   SP   -> the dark-store crawl estimate, which exists on BOTH periods, so
   *           the delta is real and like-for-like.
   */
  const portal = str(cur["basis"]) === "portal_actual";
  const onMrp = basis === "mrp";

  /*
   * The previous period is on the portal basis too when the server managed to
   * fetch it, which is what makes a delta here like-for-like.
   *
   * These three tiles used to read "no comparison" on a brand with a full
   * fortnight of portal history: the current row was portal actuals, the
   * previous row was the crawl estimate, and comparing the two would have
   * shown a 10% jump that was a change of measurement, not of sales. The
   * server now overlays both rows, so the pair is real — but the guard stays,
   * because a previous period the portal does not cover still comes back on
   * the crawl basis and must still refuse to compare.
   */
  const portalPrev = str(prv?.["basis"]) === "portal_actual";
  const likeForLike = portal && portalPrev;

  const gmv = onMrp
    ? portal
      ? num(cur["gmv"])
      : num(cur["gmv_mrp"])
    : num(cur["gmv_est"]);
  const gmvPrev = onMrp
    ? portal
      ? likeForLike
        ? num(prv?.["gmv"])
        : null
      : num(prv?.["gmv_mrp"])
    : num(prv?.["gmv_est"]);

  const units = portal ? num(cur["units"]) : num(cur["units_est"]);
  const unitsPrev = portal
    ? likeForLike
      ? num(prv?.["units"])
      : null
    : num(prv?.["units_est"]);

  const drr = portal
    ? num(cur["drr"])
    : days && units !== null
      ? units / days
      : null;
  const prevDays =
    w?.previous_start && w.previous_end
      ? daysBetween(w.previous_start, w.previous_end)
      : null;
  const drrPrev = portal
    ? likeForLike
      ? num(prv?.["drr"])
      : null
    : unitsPrev === null || !prevDays
      ? null
      : unitsPrev / prevDays;

  const osa = num(cur["osa_unweighted"]);
  const osaPrev = compare ? num(prv?.["osa_unweighted"]) : null;
  const osaW = num(cur["osa_weighted"]);
  const osaWPrev = compare ? num(prv?.["osa_weighted"]) : null;

  const fill = q.data?.fill_rate ?? null;
  const fillBasis = q.data?.fill_rate_basis;
  /*
   * Fill rate is measured on the CLOSE date now, at the client's definition:
   * the POs that closed inside the selected period, delivered over ordered.
   * Every one of them has a final outcome, so the settlement frontier, the
   * retreat to a settled window and the in-flight note that explained the
   * retreat are all gone with the order-date model that needed them.
   */
  const closesTo = fillBasis?.last_close ?? null;

  /** The foot line for the three portal tiles: either the comparison, or the
   *  reason there is not one. A tile with no stated basis is the defect this
   *  rebuild exists to remove. */
  const portalFoot = portal
    ? likeForLike
      ? `vs ${cmpLabel} · portal actuals${onMrp ? " at MRP" : ""}, both periods`
      : `portal actuals${onMrp ? " at MRP" : ""} · no comparison: the previous window is crawl-basis only`
    : compare
      ? `vs ${cmpLabel} · dark-store crawl estimate`
      : "no comparison data";

  return (
    <>
      <Kpis>
        <Kpi
          label="GMV"
          value={fmt(gmv, "money")}
          title={
            onMrp && portal
              ? "SUM(sales_value_actual) — the brand's own portal download, all listings, measured not estimated, at MRP"
              : onMrp
                ? "SUM(COALESCE(mrp_sales_actual, final_est_sales_mrp)) — dark-store crawl, at MRP"
                : "SUM(final_est_sales_sp) — dark-store crawl estimate at the selling price, on both periods so the change is like-for-like"
          }
          delta={<Delta cur={gmv} prev={gmvPrev} good="up" />}
          foot={portalFoot}
        />
        <Kpi
          label="Units"
          value={fmt(units, "int")}
          title={
            portal
              ? "SUM(actual_units_sold) — portal selling units (a 4-pack counts as one unit, not four bottles)"
              : "SUM(final_est_units) — the dark-store crawl estimate"
          }
          delta={<Delta cur={units} prev={unitsPrev} good="up" />}
          // Its own caption rather than portalFoot, because "selling units" is
          // the thing worth saying here — but it has to follow the same
          // like-for-like rule, or the tile prints a delta and denies having
          // one in the same breath, which is what it did.
          foot={
            portal
              ? likeForLike
                ? `vs ${cmpLabel} · portal selling units, both periods`
                : "portal selling units · no comparison: the previous window is crawl-basis only"
              : compare
                ? `vs ${cmpLabel} · crawl estimate`
                : "no comparison data"
          }
        />
        <Kpi
          label="DRR (units/day)"
          value={fmt(drr, "int")}
          title="SUM(units) / CALENDAR days in the window — not days in stock, which is a different and much flattering denominator"
          delta={<Delta cur={drr} prev={drrPrev} good="up" />}
          foot={
            days === null ? "units ÷ calendar days" : `${days} days in period`
          }
        />
        <Kpi
          label="OSA % (unweighted)"
          value={
            <span style={{ color: bandColor(osa) }}>{fmt(osa, "pct")}</span>
          }
          title="Pooled store-days: SUM(osa_numerator) / SUM(osa_denominator) across the window. Every SKU counts equally."
          delta={<Delta cur={osa} prev={osaPrev} good="up" pp />}
          foot={compare ? `vs ${cmpLabel}` : "no comparison data"}
        />
        <Kpi
          label="OSA % (sales-weighted)"
          value={
            <span style={{ color: bandColor(osaW) }}>{fmt(osaW, "pct")}</span>
          }
          title="Each SKU counts in proportion to what it sells: SUM(osa_absolute × sales weight) / SUM(weight), pooled over the window. A gap to the unweighted figure means the holes are in the SKUs that actually sell."
          delta={<Delta cur={osaW} prev={osaWPrev} good="up" pp />}
          foot={compare ? `vs ${cmpLabel}` : "no comparison data"}
        />
        <Kpi
          label="Fill rate"
          value={
            <span style={{ color: bandColor(fill) }}>{fmt(fill, "pct")}</span>
          }
          title="SUM(delivered_quantity) / SUM(units_ordered) across the purchase orders that CLOSED inside the selected period. An order raised in the window but not yet closed is not counted and is not a miss; it lands in whichever period it closes in."
          // The comparison is the equal-length window BEFORE the settled one,
          // not the page's own previous window: fill rate is the one figure
          // that may have been shifted off the picker, and comparing a shifted
          // current against an unshifted previous would put two different
          // settlement lags side by side and call the gap performance.
          delta={
            <Delta
              cur={fill}
              prev={q.data?.fill_rate_previous ?? null}
              good="up"
              pp
            />
          }
          /*
           * Say WHY the window moved, not just that it did.
           *
           * "fixed window 2026-08-27 to 2026-09-02, not your selection" was
           * read as a stale page. It was correct — 3 Sep was 86% closed
           * against a 90% bar — but a date a week behind the picker, with no
           * reason beside it, looks like a feed that has stopped. The cause is
           * the whole explanation, so it goes in the tile.
           */
          foot={
            fillBasis?.closed_lines
              ? `${fmt(fillBasis.closed_delivered_quantity, "int")} of ${fmt(fillBasis.closed_units_ordered, "int")} units on ${fmt(fillBasis.closed_lines, "int")} POs closed in this period${closesTo ? `, to ${closesTo}` : ""}`
              : "no purchase order closed in this period"
          }
        />
      </Kpis>

      <PlanStrip customer={customer} range={range} basis={basis} />
      <ActionsCard customer={customer} range={range} basis={basis} />

      {/*
        The server's own statement of what the figures are measured on. It is
        rendered verbatim rather than paraphrased: when the API changes basis,
        this line changes with it, and a hand-written caption would not.
      */}
      {q.data?.basis_note !== undefined && q.data.basis_note !== "" && (
        <section className="card">
          <div className="card-head">
            <span className="card-title">
              What these figures are measured on
            </span>
            <span className="spacer" />
            <span className="asof">from the server, not asserted here</span>
          </div>
          <div className="panel-body">
            <div className="finding-meta">{q.data.basis_note}</div>
          </div>
        </section>
      )}
    </>
  );
}
